import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, Loader2, Download, Mail, CalendarDays, MapPin, Ticket as TicketIcon,
  User2, FileText, ExternalLink, Plus,
} from "lucide-react";
import { toast } from "sonner";
import {
  staffOrdersApi, staffTicketPdfUrl, staffSingleTicketPdfUrl,
  formatAuDate, centsToAUD,
} from "../../lib/events";
import { extractError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import ManualIssueDialog from "../../components/ManualIssueDialog";

export default function StaffOrderView() {
  const { orderId } = useParams();
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showManualIssue, setShowManualIssue] = useState(false);
  const [resending, setResending] = useState(false);

  const load = () => {
    setLoading(true);
    staffOrdersApi.get(orderId)
      .then((o) => { setOrder(o); setError(""); })
      .catch((e) => setError(extractError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [orderId]);

  const onResend = async () => {
    setResending(true);
    try {
      const r = await staffOrdersApi.resend(orderId);
      toast.success(r.message || `Tickets resent to ${order?.buyer_email}`);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setResending(false);
    }
  };

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center" data-testid="staff-order-loading">
      <Loader2 className="animate-spin text-[var(--gt-ink-2)]" size={20} />
    </div>;
  }
  if (error || !order) {
    return (
      <div className="max-w-2xl mx-auto p-6 text-center" data-testid="staff-order-error">
        <div className="gt-label mb-2">Order unavailable</div>
        <h1 className="font-heading text-2xl font-black tracking-tight mb-2">Cannot open this order</h1>
        <p className="text-sm text-[var(--gt-ink-2)] mb-4" data-testid="staff-order-error-msg">{error}</p>
        <Link to="/dashboard/events" className="gt-btn-ghost text-sm"><ArrowLeft size={14}/> Back</Link>
      </div>
    );
  }

  const event = order.event || {};

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6" data-testid="staff-order-view">
      {showManualIssue && (
        <ManualIssueDialog
          open={showManualIssue}
          onClose={() => setShowManualIssue(false)}
          presetOrganiserId={order.organiser_id || event.organiser_id || ""}
          presetEventId={event.id || ""}
        />
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="gt-label mb-1">Order</div>
          <div className="font-mono text-sm break-all" data-testid="staff-order-id">{order.id}</div>
        </div>
        <div className="flex flex-wrap gap-2">
          {isAdmin && (
            <button type="button" onClick={() => setShowManualIssue(true)}
                    className="gt-btn-ghost text-sm" data-testid="staff-issue-ticket-btn">
              <Plus size={14}/> Add Attendee / Issue Ticket
            </button>
          )}
          <a href={staffTicketPdfUrl(order.id)} className="gt-btn-primary text-sm"
             data-testid="staff-download-all-btn" target="_blank" rel="noreferrer">
            <Download size={14}/> Download all tickets
          </a>
          <button type="button" onClick={onResend} disabled={resending}
                  className="gt-btn-ghost text-sm" data-testid="staff-resend-btn">
            {resending ? <Loader2 size={14} className="animate-spin"/> : <Mail size={14}/>}
            Resend to buyer
          </button>
        </div>
      </div>

      <div className="gt-card space-y-4" data-testid="staff-order-summary">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div>
            <div className="gt-label mb-1">Event</div>
            <div className="font-semibold" data-testid="staff-order-event-name">{event.title}</div>
            <div className="text-[var(--gt-ink-2)] text-xs flex items-start gap-1.5 mt-1">
              <CalendarDays size={13} className="mt-0.5"/>
              <span>{formatAuDate(event.start_at, event.timezone)}</span>
            </div>
            <div className="text-[var(--gt-ink-2)] text-xs flex items-start gap-1.5 mt-1">
              <MapPin size={13} className="mt-0.5"/>
              <span>{event.venue_name}{event.state ? `, ${event.state}` : ""}
                {event.venue_address && <><br/><span className="text-[var(--gt-ink-3)]">{event.venue_address}</span></>}
              </span>
            </div>
          </div>
          <div>
            <div className="gt-label mb-1">Buyer</div>
            <div className="font-semibold flex items-center gap-1.5" data-testid="staff-order-buyer-name">
              <User2 size={14}/> {order.buyer_name}
            </div>
            <div className="text-xs text-[var(--gt-ink-2)]" data-testid="staff-order-buyer-email">{order.buyer_email}</div>
            <div className="text-xs text-[var(--gt-ink-2)]" data-testid="staff-order-buyer-phone">
              📞 {order.buyer_phone || <span className="italic text-[var(--gt-ink-3)]">Not provided</span>}
            </div>
            <div className="gt-label mt-3 mb-1">Payment</div>
            <div className="text-xs">Status: <span className="font-semibold" data-testid="staff-order-status">{order.status}</span></div>
            {order.stripe_session_id && (
              <div className="text-[10px] font-mono text-[var(--gt-ink-3)] break-all">Stripe: {order.stripe_session_id}</div>
            )}
          </div>
        </div>

        <div className={`grid grid-cols-2 gap-3 text-xs border-t border-[var(--gt-line)] pt-3 ${isAdmin ? "sm:grid-cols-4" : "sm:grid-cols-2"}`} data-testid="staff-order-financial">
          <div><div className="gt-label mb-0.5">Subtotal</div><div className="font-mono">{centsToAUD(order.gross_ticket_cents||0)}</div></div>
          {isAdmin && <div><div className="gt-label mb-0.5">Service fee</div><div className="font-mono">{centsToAUD(order.service_charge_cents||order.fee_cents||0)}</div></div>}
          {isAdmin && <div><div className="gt-label mb-0.5">GST</div><div className="font-mono">{centsToAUD(order.gst_amount_cents||order.gst_cents||0)}</div></div>}
          <div><div className="gt-label mb-0.5">Total paid</div><div className="font-mono font-bold" data-testid="staff-order-total">{centsToAUD(order.amount_total_cents)}</div></div>
        </div>
      </div>

      <div>
        <div className="gt-label mb-3">Tickets ({order.tickets.length})</div>
        <div className="border-2 border-[var(--gt-ink)] bg-white divide-y divide-[var(--gt-line)]" data-testid="staff-tickets-list">
          {order.tickets.map((t) => (
            <div key={t.id} className="p-3 flex flex-wrap items-center justify-between gap-3" data-testid={`staff-ticket-row-${t.code}`}>
              <div>
                <div className="font-semibold text-sm flex items-center gap-2">
                  <TicketIcon size={14}/>
                  {t.ticket_type_name}
                  <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 border border-[var(--gt-line)] rounded-sm">
                    {t.seat_label ? `${t.section || ""} ${t.row_label ? "Row " + t.row_label + " " : ""}${t.seat_label}`.trim() : "GA"}
                  </span>
                </div>
                <div className="text-xs text-[var(--gt-ink-2)] font-mono mt-0.5" data-testid={`staff-ticket-code-${t.code}`}>
                  {t.code} · {t.status}
                </div>
              </div>
              <a href={staffSingleTicketPdfUrl(order.id, t.id)} target="_blank" rel="noreferrer"
                 className="gt-btn-ghost text-xs" data-testid={`staff-ticket-pdf-${t.code}`}>
                <FileText size={12}/> PDF
              </a>
            </div>
          ))}
        </div>
      </div>

      {event.slug && (
        <div className="flex flex-wrap items-center gap-2" data-testid="staff-order-related-links">
          {event.id && (
            <>
              <a
                href={`/dashboard/events/${event.id}/reports`}
                className="gt-btn-ghost text-xs"
                data-testid="staff-order-view-event-report"
              >
                <FileText size={12}/> View event report
              </a>
              <a
                href={`/dashboard/events/${event.id}/reports?tab=attendees`}
                className="gt-btn-ghost text-xs"
                data-testid="staff-order-view-attendees"
              >
                <User2 size={12}/> View attendees
              </a>
            </>
          )}
          <a href={`/e/${event.slug}`} target="_blank" rel="noreferrer" className="gt-btn-ghost text-xs" data-testid="staff-order-event-link">
            <ExternalLink size={12}/> View public event
          </a>
        </div>
      )}
    </div>
  );
}
